/**
 * CalendarConnections — manage external calendar connections.
 *
 * Displays connected external calendar providers with status indicators,
 * connect/disconnect buttons, and sync error messages. Initiates OAuth
 * via oauthService for connecting new providers and revokes tokens on disconnect.
 *
 * @see Requirements 5.6, 6.1, 6.6, 6.7, 17.5
 */

import { useState, useCallback, useEffect } from 'react';
import { useCalendarStore } from '../../stores';
import type { CalendarProvider, ExternalCalendarConnection } from '../../types/calendar';
import { oauthService } from '../../services/oauthService';
import { Button } from '../ui/Button';

export interface CalendarConnectionsProps {
  /** Called when a connection is added or removed to refresh parent state. */
  onConnectionChange?: () => void;
}

/** Maximum external connections allowed. */
const MAX_CONNECTIONS = 5;

/** Provider display configuration. */
const PROVIDER_CONFIG: Record<CalendarProvider, { label: string; iconColor: string }> = {
  google: { label: 'Google Calendar', iconColor: '#4285F4' },
  microsoft: { label: 'Microsoft Outlook', iconColor: '#0078D4' },
  apple: { label: 'Apple Calendar', iconColor: '#333333' },
};

/** Available providers for connecting. */
const AVAILABLE_PROVIDERS: CalendarProvider[] = ['google', 'microsoft', 'apple'];

/**
 * Format a relative time string for last sync display.
 */
function formatLastSync(isoStr: string | null): string {
  if (!isoStr) return 'Never synced';
  const syncDate = new Date(isoStr);
  const now = new Date();
  const diffMs = now.getTime() - syncDate.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

export function CalendarConnections({ onConnectionChange }: CalendarConnectionsProps) {
  const connections = useCalendarStore((s) => s.connections);
  const syncStatus = useCalendarStore((s) => s.syncStatus);
  const loadConnections = useCalendarStore((s) => s.loadConnections);
  const disconnectCalendar = useCalendarStore((s) => s.disconnectCalendar);
  const syncConnection = useCalendarStore((s) => s.syncConnection);
  const error = useCalendarStore((s) => s.error);

  const [connectingProvider, setConnectingProvider] = useState<CalendarProvider | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Load connections on mount
  useEffect(() => {
    void loadConnections();
  }, [loadConnections]);

  const isAtLimit = connections.length >= MAX_CONNECTIONS;

  // Determine which providers are already connected
  const connectedProviders = new Set(connections.map((c) => c.provider));

  /**
   * Initiate OAuth flow for a provider.
   */
  const handleConnect = useCallback(
    async (provider: CalendarProvider) => {
      setActionError(null);
      setConnectingProvider(provider);

      try {
        const { authUrl } = await oauthService.initiateOAuth(provider);
        // Redirect user to the provider's consent screen
        window.location.href = authUrl;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to initiate connection.';
        setActionError(message);
        setConnectingProvider(null);
      }
    },
    []
  );

  /**
   * Disconnect an external calendar connection.
   * Revokes tokens via API, removes provider events from local store,
   * and removes connection from the UI.
   */
  const handleDisconnect = useCallback(
    async (connectionId: string) => {
      setActionError(null);
      setDisconnectingId(connectionId);

      try {
        await disconnectCalendar(connectionId);
        onConnectionChange?.();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to disconnect calendar.';
        setActionError(message);
      } finally {
        setDisconnectingId(null);
      }
    },
    [disconnectCalendar, onConnectionChange]
  );

  /**
   * Manually trigger a sync for a connection.
   */
  const handleSync = useCallback(
    async (connectionId: string) => {
      setActionError(null);
      await syncConnection(connectionId);
    },
    [syncConnection]
  );

  /**
   * Re-authorize a revoked connection by initiating a new OAuth flow for the provider.
   */
  const handleReauthorize = useCallback(
    async (connection: ExternalCalendarConnection) => {
      setActionError(null);
      setConnectingProvider(connection.provider);

      try {
        // Disconnect the old revoked connection first
        await oauthService.revokeConnection(connection.connectionId);
        // Then start a fresh OAuth flow
        const { authUrl } = await oauthService.initiateOAuth(connection.provider);
        window.location.href = authUrl;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to initiate re-authorization.';
        setActionError(message);
        setConnectingProvider(null);
      }
    },
    []
  );

  /**
   * Get the display status for a connection based on sync status.
   */
  function getConnectionStatus(connection: ExternalCalendarConnection): {
    label: string;
    className: string;
  } {
    const info = syncStatus[connection.connectionId];
    const status = info?.status ?? connection.status;

    switch (status) {
      case 'active':
        return { label: 'Active', className: 'bg-success/15 text-success' };
      case 'syncing':
        return { label: 'Syncing...', className: 'bg-info/15 text-info' };
      case 'error':
        return { label: 'Error', className: 'bg-error/15 text-error' };
      case 'revoked':
        return { label: 'Revoked', className: 'bg-warning/15 text-warning' };
      default:
        return { label: 'Unknown', className: 'bg-bg-secondary text-text-secondary' };
    }
  }

  /**
   * Get sync error message for a connection.
   */
  function getSyncError(connectionId: string): string | null {
    const info = syncStatus[connectionId];
    if (info?.status === 'error') {
      return 'Sync failed after retries. Click sync to retry.';
    }
    return null;
  }

  return (
    <div
      className="flex flex-col gap-4 p-4"
      role="region"
      aria-label="Calendar connections"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-text-heading">
          Calendar Connections
        </h2>
        <span className="text-xs text-text-secondary">
          {connections.length}/{MAX_CONNECTIONS} connected
        </span>
      </div>

      {/* Global error */}
      {(actionError || error) && (
        <div
          role="alert"
          className="rounded-sm border border-primary-dark/30 bg-primary-dark/5 px-3 py-2 text-sm text-primary-dark"
        >
          {actionError || error}
        </div>
      )}

      {/* Connected calendars list */}
      {connections.length > 0 && (
        <div className="flex flex-col gap-2">
          {connections.map((connection) => {
            const status = getConnectionStatus(connection);
            const syncError = getSyncError(connection.connectionId);
            const providerInfo = PROVIDER_CONFIG[connection.provider];
            const isDisconnecting = disconnectingId === connection.connectionId;
            const lastSync = syncStatus[connection.connectionId]?.lastSyncAt
              ?? connection.lastSyncAt;

            return (
              <div
                key={connection.connectionId}
                className="flex flex-col gap-2 rounded-lg border border-border bg-bg-card p-3"
              >
                <div className="flex items-center gap-3">
                  {/* Provider icon */}
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${providerInfo.iconColor}15` }}
                    aria-hidden="true"
                  >
                    <ProviderIcon provider={connection.provider} />
                  </div>

                  {/* Provider info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {providerInfo.label}
                    </p>
                    <p className="text-xs text-text-secondary truncate">
                      {connection.providerEmail}
                    </p>
                  </div>

                  {/* Status badge */}
                  <span
                    className={[
                      'px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0',
                      status.className,
                    ].join(' ')}
                    aria-label={`Status: ${status.label}`}
                  >
                    {status.label}
                  </span>
                </div>

                {/* Last sync time */}
                <div className="flex items-center justify-between text-xs text-text-secondary pl-11">
                  <span>Last sync: {formatLastSync(lastSync)}</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleSync(connection.connectionId)}
                      className="text-xs text-secondary hover:text-secondary-dark transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring rounded-sm px-1"
                      aria-label={`Sync ${providerInfo.label}`}
                      disabled={syncStatus[connection.connectionId]?.status === 'syncing'}
                    >
                      {syncStatus[connection.connectionId]?.status === 'syncing'
                        ? 'Syncing...'
                        : 'Sync'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDisconnect(connection.connectionId)}
                      className="text-xs text-primary-dark hover:text-error transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring rounded-sm px-1"
                      aria-label={`Disconnect ${providerInfo.label}`}
                      disabled={isDisconnecting}
                    >
                      {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                    </button>
                  </div>
                </div>

                {/* Sync error indicator */}
                {syncError && (
                  <div
                    role="alert"
                    className="flex items-center gap-2 pl-11 text-xs text-error"
                  >
                    <SyncErrorIcon />
                    <span>{syncError}</span>
                  </div>
                )}

                {/* Re-authorization banner for revoked connections */}
                {status.label === 'Revoked' && (
                  <div
                    role="alert"
                    className="flex items-center justify-between gap-2 pl-11 rounded-md bg-warning/10 border border-warning/30 px-3 py-2 mt-1"
                  >
                    <span className="text-xs text-warning">
                      Authorization expired. Please re-connect to resume syncing.
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleReauthorize(connection)}
                      aria-label={`Re-authorize ${providerInfo.label}`}
                    >
                      Re-authorize
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {connections.length === 0 && (
        <div className="text-center py-6 text-text-secondary text-sm">
          <p>No calendars connected yet.</p>
          <p className="text-xs mt-1">
            Connect an external calendar to see your events here.
          </p>
        </div>
      )}

      {/* Connect new provider section */}
      {!isAtLimit && (
        <div className="flex flex-col gap-2 pt-2 border-t border-border-subtle">
          <p className="text-sm font-medium text-text-secondary">
            Connect a calendar
          </p>
          <div className="flex flex-wrap gap-2">
            {AVAILABLE_PROVIDERS.map((provider) => {
              const providerInfo = PROVIDER_CONFIG[provider];
              const isConnected = connectedProviders.has(provider);
              const isConnecting = connectingProvider === provider;

              return (
                <Button
                  key={provider}
                  variant="secondary"
                  size="sm"
                  onClick={() => handleConnect(provider)}
                  disabled={isConnected || isConnecting || isAtLimit}
                  aria-label={
                    isConnected
                      ? `${providerInfo.label} already connected`
                      : `Connect ${providerInfo.label}`
                  }
                >
                  <span className="flex items-center gap-1.5">
                    <ProviderIcon provider={provider} size={14} />
                    <span>
                      {isConnecting
                        ? 'Connecting...'
                        : isConnected
                          ? 'Connected'
                          : providerInfo.label}
                    </span>
                  </span>
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Capacity warning */}
      {isAtLimit && (
        <div className="text-xs text-text-secondary text-center pt-2">
          Maximum of {MAX_CONNECTIONS} calendar connections reached. Disconnect one to add another.
        </div>
      )}

      {/* OAuth permissions info (Requirement 17.5) */}
      <div className="text-xs text-text-secondary pt-2 border-t border-border-subtle">
        <p>
          Connecting a calendar grants Kira read/write access to your calendar events.
          Only calendar data is accessed — no emails, contacts, or other data.
          You can disconnect at any time to revoke access.
        </p>
      </div>
    </div>
  );
}

CalendarConnections.displayName = 'CalendarConnections';

// ─── Icons ───────────────────────────────────────────────────────────────────

function ProviderIcon({ provider, size = 16 }: { provider: CalendarProvider; size?: number }) {
  const config = PROVIDER_CONFIG[provider];

  switch (provider) {
    case 'google':
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M21.35 11.1H12v2.95h5.4c-.5 2.55-2.7 4.45-5.4 4.45-3.3 0-6-2.7-6-6s2.7-6 6-6c1.5 0 2.85.55 3.9 1.5l2.2-2.2C16.5 4.2 14.4 3.3 12 3.3c-4.8 0-8.7 3.9-8.7 8.7s3.9 8.7 8.7 8.7c4.5 0 8.4-3.3 8.4-8.7 0-.6-.05-1.2-.15-1.7l.1-.2z"
            fill={config.iconColor}
          />
        </svg>
      );
    case 'microsoft':
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <rect x="3" y="3" width="8.5" height="8.5" fill="#F25022" />
          <rect x="12.5" y="3" width="8.5" height="8.5" fill="#7FBA00" />
          <rect x="3" y="12.5" width="8.5" height="8.5" fill="#00A4EF" />
          <rect x="12.5" y="12.5" width="8.5" height="8.5" fill="#FFB900" />
        </svg>
      );
    case 'apple':
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M18.7 12.4c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.7-2-1.6-.2-3.1 1-3.9 1-.8 0-2-.9-3.3-.9-1.7 0-3.3 1-4.2 2.5C4 12.1 5.2 16.4 6.9 18.4c.8 1 1.8 2.2 3.1 2.1 1.2-.1 1.7-.8 3.2-.8 1.5 0 1.9.8 3.2.7 1.4 0 2.2-1 3-2 .9-1.4 1.3-2.7 1.3-2.8-.1 0-2.6-1-2.6-3.8l-.4.6zM16.1 4.2c.7-.8 1.1-2 1-3.2-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.5 2.9-1.3z"
            fill={config.iconColor}
          />
        </svg>
      );
  }
}

function SyncErrorIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}
